import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminMembers, useUpdateMemberKyc, useUpdateMemberStatus } from "../../hooks/useAdmin";
import Card from "../../components/ui/Card";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import Avatar from "../../components/ui/Avatar";

const fmt = (n: number) => new Intl.NumberFormat("en-NG",{style:"currency",currency:"NGN", currencyDisplay:"symbol",maximumFractionDigits:0}).format(n);
const kycColor  = (k:string) => k==="verified"?"emerald":k==="reviewing"?"amber":"red";
const statColor = (s:string) => s==="active"?"emerald":s==="suspended"?"red":"amber";

export default function AdminMembersPage() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [reason, setReason] = useState("");
  const [memid, setMemId] = useState("");

  const { data: members = [], isLoading } = useAdminMembers({ q: search || undefined, status: filter !== "all" && !filter.startsWith("kyc") ? filter : undefined, kyc: filter === "kyc-reviewing" ? "reviewing" : undefined }) as any;
  const updateKyc    = useUpdateMemberKyc();
  const updateStatus = useUpdateMemberStatus();

  const openForm = () => {
    dialogRef.current?.showModal() 
  };
  const closeForm = () => { dialogRef.current?.close() };
  const closeBtnStyle = { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer' };
  const dialogStyle = {padding: '20px', borderRadius: '12px', border: '1px solid #241917', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', width: '400px', top: '50px', left: '30px', };
  
  return (
    <div>
      <div className="flex gap-3 mb-5 flex-wrap items-end">
        <div className="flex-1 min-w-48">
          <Input label="" value={search} onChange={setSearch} placeholder="🔍 Search members…"/>
        </div>
        <Select label="" value={filter} onChange={setFilter} options={[
          {value:"all",          label:"All members"},
          {value:"active",       label:"Active"},
          {value:"pending",      label:"Pending"},
          {value:"suspended",    label:"Suspended"},
          {value:"kyc-reviewing",label:"KYC pending"},
        ]}/>
        <div className="flex gap-2">
          <Badge label={`${members.filter((m:any)=>m.status==="active").length} Active`}        color="emerald"/>
          <Badge label={`${members.filter((m:any)=>m.kyc_status==="reviewing").length} KYC`}   color="amber"/>
          <Badge label={`${members.filter((m:any)=>m.status==="suspended").length} Suspended`} color="red"/>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-400 text-sm">Loading members…</div>
        ) : (
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-50">
                {["Member","Balance","KYC","Status","Loans","Actions"].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-slate-500 border-b border-slate-200">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {members.map((m: any) => (
                <tr key={m.member_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                  onClick={() => navigate(`/admin/members/${m.member_id}`)}>
                  <td className="px-4 py-3">
                    <div className="flex gap-2.5 items-center">
                      <Avatar initials={`${m.first_name?.[0]}${m.last_name?.[0]}`} size={34}/>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{m.first_name} {m.last_name}</p>
                        <p className="text-xs text-slate-400">{m.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium text-slate-800">{fmt(m.balance||0)}</p>
                    {m.locked_amount > 0 && <p className="text-xs text-amber-600">🔒 {fmt(m.locked_amount)} locked</p>}
                  </td>
                  <td className="px-4 py-3"><Badge label={m.kyc_status}  color={kycColor(m.kyc_status)  as any}/></td>
                  <td className="px-4 py-3"><Badge label={m.status}      color={statColor(m.status)      as any}/></td>
                  <td className="px-4 py-3 text-sm text-slate-600">{m.loan_count||0}</td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1.5 flex-wrap">
                      {m.kyc_status === "reviewing" && <>
                        <Button size="sm" onClick={() => updateKyc.mutate({id:m.member_id, kyc_status:"verified"})}>✅ Approve</Button>
                        <Button size="sm" variant="danger" onClick={() => {setMemId(m.member_id); openForm()}}>Reject</Button>
                      </>}
                      {m.kyc_status === "pending"? <Button size="sm" variant="amber"    onClick={() => updateStatus.mutate({id:m.member_id, status:"suspended"})}>Review</Button> : ""}
                      {m.status === "active"    && <Button size="sm" variant="outline"    onClick={() => updateStatus.mutate({id:m.member_id, status:"suspended"})}>Suspend</Button>}
                      {m.status === "suspended" && <Button size="sm" variant="secondary"  onClick={() => updateStatus.mutate({id:m.member_id, status:"active"})}>Reinstate</Button>}
                    </div>
                  </td>
                </tr>
              ))}
              {members.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400">No members found</td></tr>
              )}
            </tbody>
          </table>
        )}

        <dialog ref={dialogRef} style={dialogStyle} >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom:'15px' }}>
            <h3><span className="text-xl">🚫📜</span> Document Reject Reason</h3>
            <button type="button" onClick={closeForm} style={closeBtnStyle}>✕</button>
          </div>
          <input value={reason} className="w-full px-3 py-2.5 border rounded-lg text-sm text-slate-800 outline-none bg-white" onChange={(e) => setReason(e.target.value)} placeholder="Enter reject reason" maxLength={255} required style={{marginBottom:"10px"}} />
          <Button variant="danger" size="sm" onClick={() => {updateKyc.mutate({id:memid, kyc_status:"rejected",reason:reason}); closeForm()}}>
            Reject KYC Document
          </Button>
        </dialog>
      </Card>
    </div>
  );
}
